/**
 * Edit form for a level.
 */
import * as React from 'react';
import { FileText, ListOrdered } from 'lucide-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Input } from '@/components/forms/Input';
import { Select } from '@/components/forms/Select';
import { getLevel, getLevels, updateLevel, reorderLevels } from '@/services/levels.service';
import type { UpdateLevelDto } from '@/types/level.types';
import { useFormValidation } from '@/hooks/useFormValidation';
import { levelValidationSchema } from '@/utils/validation-schemas-utils';
import { SlidePanelForm } from '@/components/common/SlidePanelForm';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';
import { shouldRetryUnitGet, useUnitGetNotFound } from '@/hooks/useUnitGetNotFound';
import { LevelSortableList } from './LevelSortableList';
import { LEVEL_CYCLE_FORM_OPTIONS, LEVEL_FORM_QUERY_KEYS } from './level-configs';
import {
  applyLevelFormField,
  getLevelFieldsToRevalidateAfterChange,
  levelToFormData,
  parseOptionalInt,
  resolveEducationCycle,
} from './level-form-utils';

/** Props for {@link EditLevelView}. */
interface EditLevelViewProps {
  levelId: string;
  onClose: () => void;
  onSuccess?: () => void;
}

/** Edit form for a level. */
export const EditLevelView: React.FC<EditLevelViewProps> = ({ levelId, onClose, onSuccess }) => {
  const [formData, setFormData] = React.useState<UpdateLevelDto>({});
  /** Drag order; `null` until the user reorders (then POST reorder on submit). */
  const [orderedLevelIds, setOrderedLevelIds] = React.useState<string[] | null>(null);

  const validation = useFormValidation(levelValidationSchema);

  const {
    data: level,
    isLoading: isLoadingLevel,
    isError: isLevelError,
    error: levelError,
  } = useQuery({
    queryKey: LEVEL_FORM_QUERY_KEYS.one(levelId),
    queryFn: () => getLevel(levelId),
    retry: shouldRetryUnitGet,
  });
  useUnitGetNotFound({
    isError: isLevelError,
    error: levelError,
    catalogKey: 'levelNotFound',
    onClose,
    entityId: levelId,
  });

  const { data: allLevels = [], isLoading: isLoadingAllLevels } = useQuery({
    queryKey: LEVEL_FORM_QUERY_KEYS.all,
    queryFn: async () => (await getLevels({ pageSize: 10 })).data,
  });

  const isLoading = isLoadingLevel || isLoadingAllLevels;

  /** Hydrate and run initial validation once GET level resolves. */
  React.useEffect(() => {
    if (level) {
      const initialData = levelToFormData(level);
      setFormData(initialData);
      validation.validateInitialFormData(initialData);
    }
  }, [level]);

  const updateMutation = useMutation({
    mutationFn: (dto: UpdateLevelDto) => updateLevel(levelId, dto),
    onSuccess: () => {
      toast.success('Niveau scolaire modifié avec succès !');
      onSuccess?.();
      onClose();
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error));
    },
  });

  /** Patch one field; changing minAge also re-validates maxAge. */
  const handleFieldChange = (field: keyof UpdateLevelDto, value: string | number | undefined) => {
    const updatedData = applyLevelFormField(formData, field, value);
    setFormData(updatedData);
    validation.validateField(field, value, updatedData);
    validation.markAsTouched(field);
    validation.markAsDirty(field);

    for (const extraField of getLevelFieldsToRevalidateAfterChange(field, formData)) {
      validation.validateField(extraField, formData[extraField], updatedData);
    }
  };

  const handleBlur = (field: string) => {
    validation.markAsTouched(field);
  };

  /** Validate, persist drag order if changed, then PATCH. */
  const handleSubmit = async () => {
    if (!validation.validateForm(formData)) return;
    if (orderedLevelIds) {
      await reorderLevels(orderedLevelIds);
    }
    updateMutation.mutate(formData);
  };

  return (
    <SlidePanelForm
      formType="edit"
      title="Modifier le niveau scolaire"
      subtitle="Modifiez les informations du niveau scolaire"
      onClose={onClose}
      onSubmit={handleSubmit}
      submitLabel="Enregistrer"
      isSubmitting={updateMutation.isPending}
      isValid={validation.isFormValid()}
      isLoading={isLoading || (!!level && Object.keys(formData).length === 0)}
    >
      <div className="form-section">
        <h4 className="form-section-title">
          <FileText className="w-4 h-4" />
          Informations générales
        </h4>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Code"
            value={formData.code || ''}
            onChange={(e) => handleFieldChange('code', e.target.value)}
            onBlur={() => handleBlur('code')}
            placeholder="Ex: 6EME"
            maxLength={50}
            required
            helperText="Code unique identifiant le niveau"
            state={validation.getFieldState('code')}
            error={validation.getFieldError('code')}
          />

          <Input
            label="Libellé"
            value={formData.label || ''}
            onChange={(e) => handleFieldChange('label', e.target.value)}
            onBlur={() => handleBlur('label')}
            placeholder="Ex: Sixième"
            maxLength={255}
            required
            helperText="Nom complet du niveau"
            state={validation.getFieldState('label')}
            error={validation.getFieldError('label')}
          />
        </div>

        <Select
          label="Cycle éducatif"
          options={LEVEL_CYCLE_FORM_OPTIONS}
          value={formData.educationCycle || ''}
          placeholder="Sélectionner un cycle (optionnel)"
          onChange={(value) => handleFieldChange('educationCycle', resolveEducationCycle(value))}
          onBlur={() => handleBlur('educationCycle')}
          state={validation.getFieldState('educationCycle')}
          error={validation.getFieldError('educationCycle')}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Âge minimum"
            type="number"
            value={formData.minAge?.toString() || ''}
            onChange={(e) => handleFieldChange('minAge', parseOptionalInt(e.target.value))}
            onBlur={() => handleBlur('minAge')}
            placeholder="Ex: 11"
            min={3}
            max={25}
            state={validation.getFieldState('minAge')}
            error={validation.getFieldError('minAge')}
          />

          <Input
            label="Âge maximum"
            type="number"
            value={formData.maxAge?.toString() || ''}
            onChange={(e) => handleFieldChange('maxAge', parseOptionalInt(e.target.value))}
            onBlur={() => handleBlur('maxAge')}
            placeholder="Ex: 12"
            min={3}
            max={25}
            state={validation.getFieldState('maxAge')}
            error={validation.getFieldError('maxAge')}
          />
        </div>
      </div>

      <div className="form-section">
        <h4 className="form-section-title">
          <ListOrdered className="w-4 h-4" />
          Ordre d'affichage
        </h4>
        <p className="text-xs text-muted-foreground mb-3">
          Glissez-déposez pour ordonner les niveaux
        </p>
        <LevelSortableList
          allLevels={allLevels}
          currentLevelId={levelId}
          onOrderChange={setOrderedLevelIds}
        />
      </div>
    </SlidePanelForm>
  );
};
